/** Unterseiten der Einstellungen: Kurzbefehle/NFC, Kalender, Sicherung, Diagnose. */
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { ChevronLeft, Copy } from 'lucide-react';
import QRCode from 'qrcode';
import { allTaskTypes, plural, type Diagnostics, type LinkInfo } from '@pflanzen/shared';
import { api, path } from '../lib/api';
import { mutate, recoverDevice, useStore } from '../lib/store';
import { permission, pushSupport, isStandalone, sendTestPush, syncSubscription } from '../lib/push';
import { Button, Card, SectionTitle } from '../ui/kit';

const PERM_TEXT: Record<string, string> = {
  granted: 'erlaubt',
  denied: 'abgelehnt – in den iPhone-Einstellungen unter Mitteilungen → Pflanzen ändern',
  default: 'noch nicht gefragt',
  unsupported: 'nur in der installierten App möglich',
};

function Page({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="px-4 pb-8">
      <header className="pt-safe pb-2">
        <Link to="/einstellungen" className="-ml-2 flex items-center gap-0.5 px-2 py-2 text-[15px] font-semibold text-moss">
          <ChevronLeft className="h-5 w-5" /> Einstellungen
        </Link>
        <h1 className="font-display text-[30px] leading-tight">{title}</h1>
      </header>
      {children}
    </div>
  );
}

function useLinks(): [LinkInfo | null, (l: LinkInfo) => void] {
  const [links, setLinks] = useState<LinkInfo | null>(null);
  useEffect(() => {
    api<LinkInfo>('GET', path('links')).then(setLinks).catch(() => setLinks(null));
  }, []);
  return [links, setLinks];
}

function CopyField({ value }: { value: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard?.writeText(value).then(() => {
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        });
      }}
      className="flex w-full items-center gap-2 rounded-2xl bg-paper p-3 text-left font-mono text-xs break-all"
    >
      <span className="flex-1">{value}</span>
      <span className="shrink-0 font-sans font-semibold text-moss">{done ? '✓' : <Copy className="h-4 w-4" />}</span>
    </button>
  );
}

export function ShortcutsPage() {
  const { state } = useStore();
  const [links] = useLinks();
  if (!state) return null;
  const types = allTaskTypes(state.taskTypes);
  const plants = state.plants.filter((p) => !p.archivedAt);
  return (
    <Page title="NFC-Sticker & Siri">
      <p className="text-[15px]">
        Einmal einrichten, dann reicht es, das iPhone an den Topf zu halten oder zu sagen: <i>„Hey Siri, Calathea gegossen“</i>.
      </p>
      <SectionTitle>So geht’s (einmal pro Pflanze)</SectionTitle>
      <Card>
        <ol className="list-decimal space-y-2 pl-5 text-sm">
          <li>
            App <b>Kurzbefehle</b> öffnen → <b>+</b> → Aktion <b>„Inhalte von URL abrufen“</b>.
          </li>
          <li>
            Unten die Adresse der Pflanze einfügen (antippen = kopieren). Bei der Aktion auf <b>Methode: POST</b> stellen.
          </li>
          <li>
            Aktion <b>„Wörterbuchwert abrufen“</b> (Schlüssel <code>say</code>) und <b>„Text sprechen“</b> oder <b>„Mitteilung anzeigen“</b> dazu – dann bestätigt Siri.
          </li>
          <li>
            Kurzbefehl benennen, z. B. <b>„Calathea gegossen“</b> – das ist der Siri-Satz.
          </li>
          <li>
            Für NFC: Tab <b>Automation</b> → <b>+</b> → <b>NFC</b> → Sticker scannen → <b>Sofort ausführen</b> → den Kurzbefehl wählen.
          </li>
        </ol>
        <p className="mt-3 text-xs text-muted">NFC-Sticker (NTAG215) gibt’s um ein paar Euro. Am besten unten an den Topf kleben.</p>
      </Card>
      <SectionTitle>Adressen</SectionTitle>
      {!links ? (
        <p className="text-sm text-muted">Lädt … (braucht Internet)</p>
      ) : (
        <div className="space-y-3">
          {plants.map((p) => {
            const mine = state.tasks.filter((t) => t.plantId === p.id && t.enabled);
            return (
              <Card key={p.id} className="space-y-2">
                <div className="text-[15px] font-semibold">{p.emoji} {p.name}</div>
                {mine.map((t) => {
                  const ty = types.find((x) => x.key === t.typeKey);
                  const base = `${links.shortcutBase}/${p.slug}/${t.typeKey}`;
                  return (
                    <div key={t.id}>
                      <div className="mb-1 text-xs text-muted">
                        {ty?.icon} {ty?.doneLabel ?? t.typeKey}
                      </div>
                      <CopyField value={ty?.fingerTest ? `${base}?erde=trocken` : base} />
                    </div>
                  );
                })}
              </Card>
            );
          })}
          <p className="px-1 text-xs text-muted">
            <code>?erde=trocken</code> trägt „gegossen“ ein. Mit <code>?erde=feucht</code> wird daraus „noch feucht, später prüfen“.
            Die Adressen sind geheim – wer sie kennt, kann abhaken. Neu erzeugen unter „Sicherung & Geräte“.
          </p>
        </div>
      )}
    </Page>
  );
}

export function CalendarPage() {
  const [links] = useLinks();
  const [qr, setQr] = useState<string | null>(null);
  useEffect(() => {
    if (links) void QRCode.toDataURL(links.calendarUrl.replace(/^webcal:/, 'https:'), { margin: 1, width: 220 }).then(setQr);
  }, [links]);
  return (
    <Page title="Kalender-Abo">
      <p className="text-[15px]">
        Zweiter Weg für Erinnerungen: Die fälligen Pflege-Termine erscheinen im iPhone-Kalender und aktualisieren sich von selbst.
      </p>
      {!links ? (
        <p className="mt-4 text-sm text-muted">Lädt … (braucht Internet)</p>
      ) : (
        <>
          <a href={links.calendarUrl} className="mt-4 block">
            <Button className="w-full">📅 Kalender abonnieren</Button>
          </a>
          <SectionTitle>Oder von Hand</SectionTitle>
          <Card className="space-y-3">
            <p className="text-sm">
              iPhone-<b>Einstellungen → Apps → Kalender → Accounts → Account hinzufügen → Andere → Kalenderabo hinzufügen</b> und diese Adresse einfügen:
            </p>
            <CopyField value={links.calendarUrl} />
            {qr && <img src={qr} alt="QR-Code für das Kalender-Abo" className="mx-auto h-44 w-44 rounded-xl bg-white p-1" />}
            <p className="text-xs text-muted">
              Tipp: iOS entfernt bei Abos standardmäßig die Wecker. Die Push-Mitteilungen bleiben der Haupt-Weg; der Kalender ist die Übersicht und das Backup.
            </p>
          </Card>
        </>
      )}
    </Page>
  );
}

export function BackupPage() {
  const [msg, setMsg] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [, setLinks] = useLinks();

  const exportFile = async () => {
    setMsg('Erstelle Sicherung …');
    try {
      const data = await api<unknown>('GET', `${path('exportAll')}?fotos=1`, undefined, { timeoutMs: 120_000 });
      const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
      const file = new File([blob], `pflanzen-sicherung-${new Date().toISOString().slice(0, 10)}.json`, { type: 'application/json' });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: 'Pflanzen-Sicherung' });
      } else {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = file.name;
        a.click();
      }
      setMsg('Sicherung erstellt. Am besten in „Dateien“ oder iCloud Drive ablegen.');
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    }
  };

  const importFile = async (file: File | undefined) => {
    if (!file) return;
    if (!confirm('Sicherung einspielen? Gleiche Einträge werden überschrieben, nichts wird gelöscht.')) return;
    setMsg('Spiele Sicherung ein …');
    try {
      const json = JSON.parse(await file.text()) as unknown;
      await mutate('POST', path('importAll'), json);
      setMsg('Sicherung eingespielt ✓');
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Die Datei konnte nicht gelesen werden.');
    }
  };

  const recover = async () => {
    if (code.trim().length < 10) return setMsg('Bitte den ganzen Code eingeben (PFLZ-…).');
    if (!confirm('Dieses Gerät mit dem anderen Haushalt verbinden? Die Daten hier werden durch die des Codes ersetzt.')) return;
    try {
      await recoverDevice(code.trim(), 'iPhone');
      await syncSubscription().catch(() => false);
      setMsg('Verbunden ✓');
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    }
  };

  const rotate = async () => {
    if (!confirm('Neue geheime Adressen erzeugen? Alte NFC-/Siri-Kurzbefehle und das Kalender-Abo funktionieren dann nicht mehr.')) return;
    try {
      setLinks(await api<LinkInfo>('POST', path('linksRotate')));
      setMsg('Neue Adressen erzeugt. Kurzbefehle und Kalender bitte neu einrichten.');
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <Page title="Sicherung & Geräte">
      <p className="text-[15px]">Deine Daten liegen auf dem iPhone und zusätzlich verschlüsselt übertragen auf deinem Server bei Cloudflare.</p>
      <SectionTitle>Sicherung als Datei</SectionTitle>
      <Card className="space-y-2.5">
        <Button onClick={() => void exportFile()} className="w-full">
          💾 Sicherung exportieren (mit Fotos)
        </Button>
        <label className="flex min-h-12 w-full items-center justify-center rounded-2xl border border-line bg-card text-[15px] font-semibold">
          📂 Sicherung einspielen
          <input type="file" accept="application/json,.json" className="hidden" onChange={(e) => void importFile(e.target.files?.[0])} />
        </label>
      </Card>
      <SectionTitle>Neues iPhone / zweites Gerät</SectionTitle>
      <Card className="space-y-2.5">
        <p className="text-sm">
          Beim ersten Start hast du einen <b>Wiederherstellungs-Code</b> bekommen (PFLZ-…). Auf dem neuen Gerät hier eingeben – dann sind alle Pflanzen und Fotos wieder da.
        </p>
        <input
          className="min-h-12 w-full rounded-2xl border border-line bg-paper px-3.5 font-mono text-[16px] uppercase"
          placeholder="PFLZ-XXXX-XXXX-XXXX-XXXX"
          value={code}
          autoCapitalize="characters"
          autoCorrect="off"
          onChange={(e) => setCode(e.target.value)}
        />
        <Button tone="ghost" onClick={() => void recover()} className="w-full">
          Mit Code verbinden
        </Button>
      </Card>
      <SectionTitle>Geheime Adressen</SectionTitle>
      <Card>
        <p className="mb-2.5 text-sm">Falls jemand deine Kurzbefehl- oder Kalender-Adresse kennt, der sie nicht haben soll:</p>
        <Button tone="ghost" onClick={() => void rotate()} className="w-full">
          🔑 Neue Adressen erzeugen
        </Button>
      </Card>
      {msg && <p className="mt-4 rounded-2xl bg-moss-soft p-3 text-sm text-moss">{msg}</p>}
    </Page>
  );
}

function Check({ ok, label, detail }: { ok: boolean | null | 'none'; label: string; detail?: string }) {
  return (
    <div className="flex items-start gap-3 py-2.5">
      <span className="text-lg leading-none">{ok === 'none' ? '➖' : ok == null ? '⏳' : ok ? '✅' : '⚠️'}</span>
      <div className="min-w-0">
        <div className="text-[15px]">{label}</div>
        {detail && <div className="text-xs text-muted break-words">{detail}</div>}
      </div>
    </div>
  );
}

function ago(iso: string | null): string {
  if (!iso) return 'noch nie';
  const min = Math.round((Date.now() - Date.parse(iso)) / 60_000);
  if (min < 1) return 'gerade eben';
  if (min < 60) return `vor ${min} Min`;
  if (min < 60 * 48) return `vor ${Math.round(min / 60)} Std`;
  return `vor ${Math.round(min / 1440)} Tagen`;
}

export function DiagnosticsPage() {
  const { pending, online, lastSyncAt, error } = useStore();
  const [d, setD] = useState<Diagnostics | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [testMsg, setTestMsg] = useState<string | null>(null);
  const [swOk, setSwOk] = useState<boolean | null>(null);
  const [subOk, setSubOk] = useState<boolean | null>(null);
  const load = () => {
    api<Diagnostics>('GET', path('diagnostics'))
      .then((x) => {
        setD(x);
        setErr(null);
      })
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)));
  };
  useEffect(() => {
    load();
    if ('serviceWorker' in navigator) {
      void navigator.serviceWorker.getRegistration().then(async (reg) => {
        setSwOk(!!reg?.active);
        const sub = await reg?.pushManager?.getSubscription();
        setSubOk(!!sub);
      });
    } else {
      setSwOk(false);
      setSubOk(false);
    }
  }, []);

  const perm = permission();
  const cronFresh = d?.lastCronAt ? Date.now() - Date.parse(d.lastCronAt) < 40 * 60_000 : null;

  const test = () => {
    setTestMsg('Sende …');
    syncSubscription()
      .catch(() => false)
      .then(() => sendTestPush())
      .then((r) => {
        setTestMsg(r.ok ? 'Verschickt – die Mitteilung sollte in ein paar Sekunden kommen.' : `Nicht verschickt: ${r.error ?? 'kein Abo auf dem Server'}`);
        load();
      })
      .catch((e: unknown) => setTestMsg(e instanceof Error ? e.message : String(e)));
  };

  return (
    <Page title="Diagnose">
      <SectionTitle>Dieses iPhone</SectionTitle>
      <Card className="divide-y divide-line py-1">
        <Check ok={isStandalone()} label="Als App installiert" detail={isStandalone() ? 'läuft vom Home-Bildschirm' : 'läuft im Browser – Mitteilungen gehen nur installiert'} />
        <Check ok={swOk} label="Offline-Modus (Service Worker)" />
        <Check ok={pushSupport() === 'ok' ? perm === 'granted' : false} label="Mitteilungen erlaubt" detail={PERM_TEXT[perm] ?? String(perm)} />
        <Check ok={subOk} label="Push-Abo auf dem Gerät" />
        <Check ok={online} label="Internet" detail={`Letzter Abgleich: ${ago(lastSyncAt)}${pending.length ? ` · ${pending.length} Änderung(en) warten` : ''}`} />
        {error && <Check ok={false} label="Fehler beim Abgleich" detail={error} />}
      </Card>
      <SectionTitle>Server</SectionTitle>
      <Card className="divide-y divide-line py-1">
        {err && <Check ok={false} label="Server nicht erreichbar" detail={err} />}
        <Check ok={d ? d.push.subscriptions > 0 : null} label="Push-Abo beim Server" detail={d ? `${d.push.subscriptions} ${plural(d.push.subscriptions, 'Gerät', 'Geräte')}` : ''} />
        <Check
          ok={!d ? null : !d.push.lastSentAt ? 'none' : d.push.lastError == null || d.push.lastStatus === 201}
          label="Letzte Mitteilung"
          detail={d ? `${ago(d.push.lastSentAt)}${d.push.lastError ? ` · Fehler: ${d.push.lastError}` : ''}` : ''}
        />
        <Check ok={cronFresh} label="Zeitplaner (alle 15 Min)" detail={d ? `zuletzt ${ago(d.lastCronAt)}${d.lastCronMs != null ? ` · ${d.lastCronMs} ms` : ''}` : ''} />
        <Check ok={d ? d.weatherFetchedAt != null : null} label="Wetter" detail={d ? `geladen ${ago(d.weatherFetchedAt)}` : ''} />
        <Check ok={d ? true : null} label="Daten" detail={d ? `${d.counts.plants} ${plural(d.counts.plants, 'Pflanze', 'Pflanzen')} · ${d.counts.events} ${plural(d.counts.events, 'Eintrag', 'Einträge')} · ${d.counts.photos} ${plural(d.counts.photos, 'Foto', 'Fotos')} · v${d.version}` : ''} />
      </Card>
      <Button onClick={test} className="mt-4 w-full">
        🔔 Test-Mitteilung senden
      </Button>
      {testMsg && <p className="mt-2 text-sm text-muted">{testMsg}</p>}
    </Page>
  );
}
